import {
    Intent,
    Size,
    Spinner,
    Tag,
    TagProps,
    Tooltip
} from "@blueprintjs/core";
import { CustomUpgrade } from "in_queue/contexts/CustomUpgradesContext";
import {
    ValidationStatus,
    VALIDATION_STATE_LABEL,
    VALIDATION_STATE_TOOLTIP
} from "in_queue/utils/customUpgradeValidation";
import { CUSTOM_UPGRADE_STATUS_ARCHIVED } from "./customUpgradeConstants";
import css from "./CustomUpgradePills.module.scss";
import { VALIDATION_ICON, VALIDATION_INTENT } from "./validationStatusVisuals";

export const StatusPill: React.FC<{
    readonly status: ValidationStatus;
    readonly size?: TagProps["size"];
    readonly showTooltip?: boolean;
}> = ({ status, size = Size.LARGE, showTooltip = true }) => {
    const tooltip =
        status.state === "failed" && status.errorMessage
            ? status.errorMessage
            : VALIDATION_STATE_TOOLTIP[status.state];
    const icon =
        status.state === "validating" ? (
            <Spinner size={14} />
        ) : (
            VALIDATION_ICON[status.state]
        );

    const tag = (
        <Tag
            minimal
            round
            size={size}
            className={css["CustomUpgradePills-large"]}
            intent={VALIDATION_INTENT[status.state]}
            icon={icon}
        >
            {VALIDATION_STATE_LABEL[status.state]}
        </Tag>
    );

    return showTooltip ? <Tooltip content={tooltip}>{tag}</Tooltip> : tag;
};

interface OriginPillContent {
    readonly label: string;
    readonly intent: Intent;
    readonly tooltip: string;
}

const SHARED_ORIGIN: OriginPillContent = {
    label: "Nira Published",
    intent: Intent.PRIMARY,
    tooltip: "Nira published this upgrade for every team on this cluster."
};

const CUSTOM_ORIGIN: OriginPillContent = {
    label: "Custom",
    intent: Intent.WARNING,
    tooltip: "Your team uploaded this upgrade."
};

export const OriginPill: React.FC<{ readonly upgrade: CustomUpgrade }> = ({
    upgrade
}) => {
    const { label, intent, tooltip } = upgrade.isShared
        ? SHARED_ORIGIN
        : CUSTOM_ORIGIN;
    return (
        <Tooltip content={tooltip}>
            <Tag
                minimal
                round
                size={Size.LARGE}
                className={css["CustomUpgradePills-large"]}
                intent={intent}
            >
                {label}
            </Tag>
        </Tooltip>
    );
};

const ArchivedPill: React.FC = () => (
    <Tag minimal round intent={Intent.DANGER}>
        <span className={css["CustomUpgradePills-dot"]} aria-hidden />
        <strong>Archived</strong>
    </Tag>
);

interface CustomUpgradePillsProps {
    readonly upgrade: CustomUpgrade;
    readonly showValidation?: boolean;
    /** Size of the status pill. Defaults to `large`. */
    readonly size?: TagProps["size"];
    /** Whether the pills render their own tooltips. Suppress when a surrounding
     * element (e.g. a menu item) already provides one. */
    readonly showTooltip?: boolean;
}

export const CustomUpgradePills: React.FC<CustomUpgradePillsProps> = ({
    upgrade,
    showValidation = false,
    size,
    showTooltip = true
}) => (
    <div className={css["CustomUpgradePills"]}>
        {upgrade.status === CUSTOM_UPGRADE_STATUS_ARCHIVED && <ArchivedPill />}
        {showValidation &&
            upgrade.modelSetValidationInfo.status.state !== "noModelSet" && (
                <StatusPill
                    status={upgrade.modelSetValidationInfo.status}
                    size={size}
                    showTooltip={showTooltip}
                />
            )}
    </div>
);
